import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { resolveAssetUrl } from "../../lib/api";
import { toast } from "sonner";
import {
  ArrowRight, CheckCircle2, XCircle, Clock, Layers, Play, Lock,
  ChevronDown, ChevronUp, Mail,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const QV_LOGO =
  "https://customer-assets.emergentagent.com/job_athletic-edge-19/artifacts/7jyn9i1w_IMG_2453.PNG";
const DEFAULT_COVER =
  "https://customer-assets.emergentagent.com/job_athletic-edge-19/artifacts/z57p91jo_IMG_2623%202.PNG";

const PublicNav = () => (
  <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/5">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
      <a href="/" className="flex items-center gap-3">
        <img src={QV_LOGO} alt="QV" className="h-10 w-10 object-contain" />
        <div className="leading-tight hidden sm:block">
          <div className="qv-display text-white text-lg">QUIQUE VARGAS</div>
          <div className="text-[10px] tracking-[0.32em] text-neutral-400 uppercase">Programas</div>
        </div>
      </a>
      <a href="/programas" className="text-xs uppercase tracking-[0.22em] text-neutral-300 hover:text-white transition-colors">
        Todos los programas
      </a>
    </div>
  </header>
);

/* ---------------- Buy Card ---------------- */
const BuyCard = ({ program, onBuy, buying, email, setEmail, totalSessions }) => (
  <div className="border border-white/10 bg-[#0a0a0a] p-6 sm:p-8" data-testid="buy-card">
    <div className="flex items-baseline gap-2">
      <div className="qv-display text-white text-5xl sm:text-6xl">{Math.round(program.price_eur)}€</div>
      <div className="text-neutral-500 text-xs tracking-[0.2em] uppercase">pago único</div>
    </div>
    <div className="mt-2 text-neutral-400 text-sm">Acceso de por vida · Sin permanencia</div>

    <ul className="mt-6 space-y-3 text-sm text-neutral-200">
      {[
        `${program.duration_weeks} semanas de entrenamiento`,
        `${totalSessions} sesiones estructuradas`,
        "Vídeos explicativos de cada ejercicio",
        "Actualizaciones incluidas para siempre",
      ].map((t) => (
        <li key={t} className="flex items-start gap-2">
          <CheckCircle2 size={16} color="var(--qv-red)" className="mt-0.5 shrink-0" />
          {t}
        </li>
      ))}
    </ul>

    <form
      onSubmit={(e) => { e.preventDefault(); onBuy(); }}
      className="mt-7 space-y-3"
      data-testid="buy-form"
    >
      <label className="text-xs uppercase tracking-[0.2em] text-neutral-400 block">
        Email para recibir el acceso*
      </label>
      <div className="relative">
        <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="qv-input pl-10"
          placeholder="tucorreo@email.com"
          data-testid="buy-email-input"
          autoComplete="email"
        />
      </div>
      <button
        type="submit"
        disabled={buying}
        className="qv-btn-primary w-full justify-center disabled:opacity-60"
        data-testid="buy-submit"
      >
        {buying ? "REDIRIGIENDO..." : "COMPRAR AHORA"}
        {!buying && <ArrowRight size={18} />}
      </button>
      <p className="text-[11px] text-neutral-500 text-center leading-relaxed">
        Pago seguro con Stripe. Recibirás un email para activar tu cuenta.
      </p>
    </form>
  </div>
);

/* ---------------- Sample Session ---------------- */
const SampleBlock = ({ block }) => (
  <div className="border border-white/10 bg-[#0a0a0a] p-5 mb-3" data-testid="sample-block">
    <div className="qv-overline mb-3">{block.type}{block.name ? ` · ${block.name}` : ""}</div>
    <div className="space-y-2">
      {(block.exercises || []).map((ex, i) => (
        <div key={ex.id || i} className="flex items-start justify-between gap-3 py-2 border-b border-white/5 last:border-0">
          <div className="min-w-0">
            <div className="text-white font-semibold text-sm">{ex.name}</div>
            <div className="text-neutral-400 text-xs mt-0.5 flex flex-wrap gap-3">
              {ex.sets && <span><b className="text-neutral-300">{ex.sets}</b> series</span>}
              {ex.reps && <span><b className="text-neutral-300">{ex.reps}</b> reps</span>}
              {ex.rest && <span>Descanso {ex.rest}</span>}
              {ex.load && <span>{ex.load}</span>}
            </div>
          </div>
          {ex.youtube_url && (
            <a
              href={ex.youtube_url}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 w-8 h-8 rounded-full inline-flex items-center justify-center border border-white/15 text-white hover:border-[color:var(--qv-red)] hover:text-[color:var(--qv-red)] transition-colors"
              aria-label="Ver vídeo"
            >
              <Play size={14} />
            </a>
          )}
        </div>
      ))}
    </div>
  </div>
);

const WeekList = ({ weeks }) => {
  const [openWeek, setOpenWeek] = useState(0);
  if (!weeks?.length) return null;
  return (
    <div className="space-y-3" data-testid="weeks-list">
      {weeks.map((w, wi) => (
        <div key={w.id || wi} className="border border-white/10 bg-[#0a0a0a]">
          <button
            type="button"
            onClick={() => setOpenWeek(openWeek === wi ? -1 : wi)}
            className="w-full flex items-center justify-between p-5 text-left"
            data-testid={`week-toggle-${wi}`}
          >
            <div>
              <div className="text-[10px] tracking-[0.22em] text-neutral-500 uppercase">Semana {w.week_number}</div>
              {w.title && <div className="qv-display text-white text-lg mt-1">{w.title}</div>}
            </div>
            {openWeek === wi ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          {openWeek === wi && (
            <div className="px-5 pb-5 space-y-2">
              {(w.days || []).map((d) => (
                <div key={d.id} className="flex items-center gap-3 py-2 border-t border-white/5 first:border-0" data-testid={`week-day-${d.id}`}>
                  <div className="w-10 h-10 flex items-center justify-center border border-white/10">
                    {d.is_preview ? (
                      <Play size={14} color="var(--qv-red)" />
                    ) : d.rest ? (
                      <span className="text-[10px] text-neutral-500">OFF</span>
                    ) : (
                      <Lock size={14} className="text-neutral-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm font-semibold">{d.day}{d.title ? ` · ${d.title}` : ""}</div>
                    {!d.rest && !d.is_preview && (
                      <div className="text-[11px] text-neutral-500 mt-0.5">
                        {d.block_count} bloques · {d.exercise_count} ejercicios
                      </div>
                    )}
                    {d.is_preview && (
                      <div className="text-[11px] mt-0.5" style={{ color: "var(--qv-red)" }}>
                        Preview gratis · desplázate abajo
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default function ProgramDetail() {
  const { slug } = useParams();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [buying, setBuying] = useState(false);
  const [email, setEmail] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/programs/${slug}`);
        if (!alive) return;
        setProgram(data);
      } catch (e) {
        if (!alive) return;
        setError("Programa no encontrado.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  const onBuy = async () => {
    if (!email.trim()) {
      toast.error("Escribe tu email.");
      return;
    }
    setBuying(true);
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(`${API}/programs/${slug}/checkout`, {
        origin_url: origin,
        email: email.trim(),
      });
      window.location.href = data.url;
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se ha podido iniciar el pago.");
      setBuying(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#050505] text-neutral-500 flex items-center justify-center">Cargando...</div>;
  }
  if (error || !program) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-10">
        <div className="text-center">
          <div className="qv-overline">404</div>
          <h1 className="qv-display text-white text-3xl mt-3">{error}</h1>
          <a href="/programas" className="qv-btn-primary mt-6 inline-flex">Ver todos los programas</a>
        </div>
      </div>
    );
  }

  // Find preview day
  const previewWeek = program.weeks?.[program.preview_week_index || 0];
  const previewDay = previewWeek?.days?.find((d) => d.is_preview);
  const totalSessions = (program.weeks || []).reduce(
    (acc, w) => acc + (w.days || []).filter((d) => !d.rest).length, 0
  );

  return (
    <div className="bg-[#050505] text-white min-h-screen" data-testid="program-detail">
      <PublicNav />

      {/* HERO */}
      <section className="relative pt-32 pb-14 sm:pt-40 sm:pb-20 overflow-hidden qv-grain">
        <div className="absolute inset-0 pointer-events-none">
          <img
            src={resolveAssetUrl(program.cover_image) || DEFAULT_COVER}
            alt=""
            className="absolute right-0 top-0 h-full w-[65%] object-cover qv-hero-mask opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-3xl">
            <div className="qv-overline">PROGRAMA · {program.level || "TODOS LOS NIVELES"}</div>
            <h1 className="qv-display text-white text-5xl sm:text-6xl lg:text-7xl mt-4 leading-[0.9]" data-testid="program-title">
              {program.title}
            </h1>
            {program.subtitle && (
              <p className="mt-6 text-neutral-300 text-lg sm:text-xl leading-relaxed max-w-xl">
                {program.subtitle}
              </p>
            )}
            {program.hero_tagline && (
              <p className="mt-4 text-neutral-400 max-w-xl">
                {program.hero_tagline}
              </p>
            )}
            <div className="mt-8 flex flex-wrap gap-2">
              <span className="qv-chip"><Clock size={12} /> {program.duration_weeks} semanas</span>
              <span className="qv-chip"><Layers size={12} /> {(program.weeks || []).reduce((acc, w) => acc + (w.days?.filter((d) => !d.rest).length || 0), 0)} sesiones</span>
              <span className="qv-chip">Pago único · {Math.round(program.price_eur)}€</span>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN GRID */}
      <section className="relative pb-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-16">
            {/* Description */}
            {program.description && (
              <div>
                <div className="qv-overline mb-4">Qué es esto</div>
                <p className="text-neutral-300 leading-relaxed whitespace-pre-line">{program.description}</p>
              </div>
            )}

            {/* Outcomes */}
            {(program.outcomes || []).length > 0 && (
              <div data-testid="outcomes-section">
                <div className="qv-overline mb-4">Qué vas a conseguir</div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {program.outcomes.map((o) => (
                    <li key={o} className="flex items-start gap-3 border border-white/10 bg-[#0a0a0a] p-4">
                      <CheckCircle2 size={18} color="var(--qv-red)" className="shrink-0 mt-0.5" />
                      <span className="text-neutral-200 text-sm">{o}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* For / not for */}
            {(program.for_who?.length || program.not_for?.length) > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {program.for_who?.length > 0 && (
                  <div className="border border-white/10 bg-[#0a0a0a] p-6" data-testid="for-who">
                    <div className="qv-overline mb-4">Para ti si...</div>
                    <ul className="space-y-2">
                      {program.for_who.map((t) => (
                        <li key={t} className="flex items-start gap-2 text-sm text-neutral-200">
                          <CheckCircle2 size={16} color="var(--qv-red)" className="shrink-0 mt-0.5" /> {t}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {program.not_for?.length > 0 && (
                  <div className="border border-white/10 bg-[#0a0a0a] p-6" data-testid="not-for">
                    <div className="qv-overline mb-4">No es para ti si...</div>
                    <ul className="space-y-2">
                      {program.not_for.map((t) => (
                        <li key={t} className="flex items-start gap-2 text-sm text-neutral-400">
                          <XCircle size={16} className="shrink-0 mt-0.5 text-neutral-500" /> {t}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Weeks overview */}
            {(program.weeks || []).length > 0 && (
              <div data-testid="content-section">
                <div className="qv-overline mb-4">Qué incluye · Contenido</div>
                <WeekList weeks={program.weeks} />
              </div>
            )}

            {/* Preview session */}
            {previewDay && previewDay.blocks && (
              <div data-testid="preview-section">
                <div className="qv-overline mb-2">Preview gratis</div>
                <h2 className="qv-display text-white text-3xl sm:text-4xl mb-6">
                  Sesión ejemplo: <span style={{ color: "var(--qv-red)" }}>{previewDay.title || previewDay.day}</span>
                </h2>
                {previewDay.blocks.map((b, i) => <SampleBlock key={b.id || i} block={b} />)}
              </div>
            )}

            {/* Testimonials */}
            {(program.testimonials || []).length > 0 && (
              <div>
                <div className="qv-overline mb-6">Testimonios</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {program.testimonials.map((t, i) => (
                    <div key={i} className="border border-white/10 bg-[#0a0a0a] p-6">
                      <p className="text-neutral-200 text-sm leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <div className="qv-display text-lg" style={{ color: "var(--qv-red)" }}>{t.name}</div>
                        {t.role && <div className="text-xs text-neutral-400 mt-1">{t.role}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* FAQ */}
            {(program.faq || []).length > 0 && (
              <div data-testid="faq-section">
                <div className="qv-overline mb-4">Preguntas frecuentes</div>
                <div className="space-y-3">
                  {program.faq.map((f, i) => (
                    <details key={i} className="border border-white/10 bg-[#0a0a0a] p-5 group">
                      <summary className="cursor-pointer flex items-center justify-between text-white font-semibold text-sm sm:text-base">
                        {f.q}
                        <ChevronDown size={16} className="transition-transform group-open:rotate-180" />
                      </summary>
                      <p className="mt-3 text-neutral-400 text-sm leading-relaxed whitespace-pre-line">{f.a}</p>
                    </details>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sticky buy card */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              <BuyCard program={program} onBuy={onBuy} buying={buying} email={email} setEmail={setEmail} totalSessions={totalSessions} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
